import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from './AuthContext';

const FacilityContext = createContext(null);

export const FacilityProvider = ({ children }) => {
  const { user, accessToken, impersonation, isAuthenticated } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [currentFacility, setCurrentFacility] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchFacilities = useCallback(async () => {
    if (!isAuthenticated()) return;
    setLoading(true);
    try {
      const res = await apiClient.get('/api/facilities/');
      setFacilities(res.data);
      // Selection precedence on (re)load:
      //   1. An explicit in-session/persisted choice (localStorage). A user
      //      who switched facilities this session shouldn't be yanked back to
      //      the default — honor their pick first.
      //   2. The org's default landing facility (is_default_landing === true),
      //      set by a portfolio owner/manager. Applies on a fresh load/login
      //      when there's no valid persisted selection.
      //   3. Fallback: the first facility in the list (already display_order-
      //      then-creation-order sorted by the API).
      const savedId = localStorage.getItem('currentFacilityId');
      const match = res.data.find((f) => f._id === savedId);
      const defaultLanding = res.data.find((f) => f.is_default_landing === true);
      if (match) {
        setCurrentFacility(match);
      } else if (defaultLanding) {
        setCurrentFacility(defaultLanding);
        localStorage.setItem('currentFacilityId', defaultLanding._id);
      } else if (res.data.length > 0) {
        setCurrentFacility(res.data[0]);
        localStorage.setItem('currentFacilityId', res.data[0]._id);
      } else {
        setCurrentFacility(null);
      }
    } catch (err) {
      console.error('Failed to fetch facilities:', err);
      setFacilities([]);
      setCurrentFacility(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Refetch whenever the auth token or impersonation state changes — this
  // covers fresh login, logout, and platform-admin support sessions that
  // swap the access token mid-session.
  useEffect(() => {
    if (user && accessToken) {
      fetchFacilities();
    } else {
      setFacilities([]);
      setCurrentFacility(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken, impersonation?.organization_id]);

  const switchFacility = (facilityId) => {
    const facility = facilities.find((f) => f._id === facilityId);
    if (facility) {
      setCurrentFacility(facility);
      localStorage.setItem('currentFacilityId', facilityId);
    }
  };

  const facilityId = currentFacility?._id || '';

  return (
    <FacilityContext.Provider
      value={{
        facilities,
        currentFacility,
        facilityId,
        loading,
        switchFacility,
        fetchFacilities,
        refreshFacilities: fetchFacilities,
      }}
    >
      {children}
    </FacilityContext.Provider>
  );
};

export const useFacility = () => {
  const context = useContext(FacilityContext);
  if (!context) {
    throw new Error('useFacility must be used within a FacilityProvider');
  }
  return context;
};

export default FacilityContext;
